<template>
  <v-card class="mb-8">
    <v-card-title>レシートプリンタ</v-card-title>
    <v-card-text>
      <v-text-field
        ref="printIpAddressField"
        :value="form.printIpAddress ? form.printIpAddress.split(':')[0] : ''"
        :error="!form.printIpAddress"
        label="プリンタ IPアドレス"
        placeholder="IPアドレス"
        minlength="7"
        maxlength="15"
        pattern="^((\d{1,2}|1\d\d|2[0-4]\d|25[0-5])\.){3}(\d{1,2}|1\d\d|2[0-4]\d|25[0-5])$"
        :rules="[
          value => /^((\d{1,2}|1\d\d|2[0-4]\d|25[0-5])\.){3}(\d{1,2}|1\d\d|2[0-4]\d|25[0-5])$/.test(value) || '無効な値です',
        ]"
        required
        @input="changePrintIpAddress($event)"
      />

      <div class="d-flex align-center flex-wrap mb-4">
        <v-btn
          x-small
          outlined
          color="grey darken-1"
          class="mr-1 mb-1"
          :href="printerWebConfigUrl"
          :disabled="!printerWebConfigUrl"
          target="_blank"
        >
          WebConfig
        </v-btn>
        <v-btn
          x-small
          outlined
          color="warning"
          class="mr-1 mb-1"
          :href="printerCertUrl"
          :disabled="!printerCertUrl"
          target="_blank"
        >
          証明書を受け入れる
        </v-btn>
        <v-btn
          x-small
          outlined
          color="primary"
          class="mr-1 mb-1"
          :loading="checkConnectionStatus.inProgress"
          @click="testConnection"
        >
          接続テスト
        </v-btn>
        <v-slide-x-transition>
          <span
            v-show="checkConnectionStatus.on && !checkConnectionStatus.inProgress"
            class="mr-1"
          >
            <v-icon
              v-if="isConnected"
              small
              color="success"
            >
              {{ icons.mdiCheckCircleOutline }}
            </v-icon>
            <v-icon
              v-else
              small
              color="error"
            >
              {{ icons.mdiCloseCircleOutline }}
            </v-icon>
          </span>
        </v-slide-x-transition>
        <v-btn
          x-small
          outlined
          color="success"
          class="mr-1 mb-1"
          @click="openPrinterDiagnostic"
        >
          診断
        </v-btn>
      </div>
      <printer-diagnostic-dialog />

      <v-divider class="my-4"></v-divider>

      <!-- サーバー経由印刷（SDP）設定。認証情報（ポーリングID/パスワード）はキッチンプリンタ設定カードと共通 -->
      <v-switch
        v-model="form.receiptSdpEnabled"
        label="サーバー経由で印刷する（サーバーダイレクトプリント）"
        hint="ONにすると会計伝票・領収書などをプリンタがサーバーから取得して印刷します。プリンタのWebConfigにサーバーURL・ポーリングID・パスワード（キッチンプリンタ設定の「SDP認証情報」参照）の設定が必要です"
        persistent-hint
        class="mt-0"
      />
      <v-text-field
        v-model="form.receiptSdpDeviceId"
        label="プリンタ識別子（devid）"
        hint="レシートプリンタがHub機本体の場合は local_printer。Hub配下に登録した場合はWebConfig上の登録名"
        persistent-hint
        :disabled="!form.receiptSdpEnabled"
        class="mt-4"
      />

      <!--
        会計とキッチンで同じ devid を指すと、両方の伝票が同じ物理プリンタに出る。
        1台運用なら正しいが、別々のプリンタを意図した誤設定の可能性が高いため警告する（保存は許可）。
      -->
      <v-alert
        v-if="conflictingKitchenPrinterNames.length > 0"
        type="warning"
        dense
        text
        class="mt-3 mb-0"
      >
        この識別子（{{ form.receiptSdpDeviceId }}）はキッチンプリンタ「{{ conflictingKitchenPrinterNames.join('、') }}」と同じです。
        会計伝票とキッチン伝票が<strong>同じプリンタに印刷</strong>されます。別のプリンタに出したい場合は devid を変えてください
        （1台で兼用する場合はこのままで問題ありません）。
      </v-alert>

      <v-divider class="my-4"></v-divider>

      <v-file-input
        ref="receiptPreviewBackgroundImageField"
        v-model="form.receiptPreviewBackgroundImage"
        accept="image/*"
        label="レシートプレビュー背景"
        :rules="[lessThanOrEqualTo2Gb]"
        show-size
      >
        <template #append-outer>
          <v-btn
            color="secondary"
            :ripple="false"
            :disabled="!(currentClub.receiptPreviewBackgroundImage || form.receiptPreviewBackgroundImage)"
            @click="previewBackgroundImage('receiptPreviewBackgroundImage')"
          >
            プレビュー
          </v-btn>
        </template>
      </v-file-input>

      <div class="d-flex justify-end mt-4">
        <v-btn
          color="primary"
          :ripple="false"
          :loading="isSubmitting"
          :disabled="hasError"
          @click="update"
        >
          保存
        </v-btn>
      </div>
    </v-card-text>

    <!-- プレビューダイアログ -->
    <v-dialog
      :value="!!onPreviewKey && !!backgroundImagePreviewUrl"
      max-width="440"
      @input="value => { if (!value) { onPreviewKey = null; backgroundImagePreviewUrl = null } }"
    >
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center">
          レシートプレビュー背景
          <v-btn
            icon
            @click="onPreviewKey = null; backgroundImagePreviewUrl = null"
          >
            <v-icon>{{ icons.mdiClose }}</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="d-flex justify-center">
          <div
            class="preview-bg"
            :style="{ backgroundImage: `url(${backgroundImagePreviewUrl})` }"
          ></div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-snackbar
      :value="updated"
      bottom
      right
      vertical
      :timeout="-1"
    >
      <p class="mb-0">
        プリンタ設定の変更を反映するため、リロードしてください
      </p>
      <template #action="{ attrs }">
        <v-btn
          :ripple="false"
          color="primary"
          v-bind="attrs"
          @click="$router.go(0)"
        >
          リロード
        </v-btn>
      </template>
    </v-snackbar>
  </v-card>
</template>

<script>
import {
  ref, reactive, computed, defineComponent, getCurrentInstance, onMounted,
} from '@vue/composition-api'
import {
  mdiCheckCircleOutline, mdiCloseCircleOutline, mdiPrinterSearch, mdiClose,
} from '@mdi/js'
import { cloneDeep } from 'lodash'
import camelcaseKeys from 'camelcase-keys'
import ClubApi from '@/api/waiter/Club'
import KitchenPrinterApi from '@/api/waiter/KitchenPrinter'
import useCurrentData, { setCurrentClub } from '@/views/composable/useCurrentData'
import useReceiptPrinterConnection from '@/views/composable/useReceiptPrinterConnection'
import PrinterDiagnosticDialog from '@/views/components/printer/PrinterDiagnosticDialog.vue'
import { openPrinterDiagnostic } from '@/views/composable/usePrinterDiagnostic'

export default defineComponent({
  components: {
    PrinterDiagnosticDialog,
  },
  setup() {
    const vm = getCurrentInstance().proxy
    const { currentClub } = useCurrentData()
    const { isConnected, checkConnection } = useReceiptPrinterConnection()

    const printIpAddressField = ref(null)
    const receiptPreviewBackgroundImageField = ref(null)
    const isSubmitting = ref(false)
    const onPreviewKey = ref(null)
    const updated = ref(false)
    const backgroundImagePreviewUrl = ref(null)

    // updateClub は club 全フィールドを送信するため、currentClub 全体を form に持って
    // 他項目を保持したまま printIpAddress / プレビュー背景のみ更新する（Club.vue と同じ挙動）
    const form = ref({
      ...cloneDeep(currentClub.value),
      receiptPreviewBackgroundImage: null,
    })

    // SDP有効なキッチンプリンタ一覧（devid 衝突チェック用）。設定画面表示時に1回取得する。
    const sdpKitchenPrinters = ref([])
    onMounted(async () => {
      try {
        const res = await KitchenPrinterApi.getKitchenPrinterSettings()
        sdpKitchenPrinters.value = (res?.data?.kitchenPrinters?.data || [])
          .filter(p => p.attributes?.sdpEnabled)
      } catch {
        // 取得失敗時は警告を出さないだけ（保存は妨げない）
        sdpKitchenPrinters.value = []
      }
    })

    // 会計の devid が SDP有効キッチンプリンタの devid と重複している場合に警告する。
    // SDP無効のキッチンプリンタはポーリングしない（devidルーティング対象外）ため除外済み。
    const conflictingKitchenPrinterNames = computed(() => {
      if (!form.value.receiptSdpEnabled) return []
      const devid = (form.value.receiptSdpDeviceId || '').trim()
      if (!devid) return []

      return sdpKitchenPrinters.value
        .filter(p => p.attributes?.sdpDeviceId === devid)
        .map(p => p.attributes?.name)
        .filter(Boolean)
    })

    const checkConnectionStatus = reactive({
      on: false,
      inProgress: false,
    })

    const resetCheckConnectionStatus = () => {
      checkConnectionStatus.on = false
      checkConnectionStatus.inProgress = false
      isConnected.value = false
    }

    const changePrintIpAddress = ipAddress => {
      resetCheckConnectionStatus()
      form.value.printIpAddress = ipAddress
    }

    const testConnection = async () => {
      isConnected.value = false
      checkConnectionStatus.on = true
      checkConnectionStatus.inProgress = true
      // 設定画面の「接続テスト」は端末ごとの無効設定にかかわらず必ず実行する
      await checkConnection(form.value.printIpAddress, { force: true })
      checkConnectionStatus.inProgress = false
    }

    const previewBackgroundImage = key => {
      let url
      if (form.value[key]) {
        url = URL.createObjectURL(form.value[key])
      } else {
        url = currentClub.value[key]
      }

      if (url) {
        backgroundImagePreviewUrl.value = url
        onPreviewKey.value = key
      }
    }

    const lessThanOrEqualTo2Gb = file => {
      return !file || file.size <= (2 * 1024 * 1024 * 1024) || '2GB以下のサイズにしてください'
    }

    const hasError = computed(() => {
      return printIpAddressField?.value?.hasError
        || receiptPreviewBackgroundImageField?.value?.hasError
    })

    const printerCertUrl = computed(() => {
      const ip = form.value?.printIpAddress
      if (!ip) return null
      const cleanIp = ip.split(':')[0]

      return `https://${cleanIp}:8043/cgi-bin/epos/service.cgi`
    })
    const printerWebConfigUrl = computed(() => {
      const ip = form.value?.printIpAddress
      if (!ip) return null
      const cleanIp = ip.split(':')[0]

      return `https://${cleanIp}/`
    })

    const update = async () => {
      isSubmitting.value = true
      // IPアドレスが変わると印刷用の共有接続(Layout provide)が古いIPのままになるためリロードを促す
      const ipChanged = (currentClub.value?.printIpAddress || '') !== (form.value.printIpAddress || '')
      // SDPのON/OFFを切り替えると、Layoutが起動時に張った直結接続(printer)が残ったままになる。
      // 二重印刷はしない(印刷トリガーはreceiptSdpEnabledをリアクティブに見て分岐する)が、
      // 旧経路の接続がゾンビとして残り同時接続枠を占有するため、リロードで掃除を促す。
      const sdpChanged = !!currentClub.value?.receiptSdpEnabled !== !!form.value.receiptSdpEnabled
      const res = await ClubApi.updateClub(form.value)

      if (res?.data?.club) {
        setCurrentClub(camelcaseKeys(res.data.club))
        vm.$toast.success('更新しました')
        if (ipChanged || sdpChanged) updated.value = true
      } else {
        vm.$toast.error('更新できませんでした')
      }

      isSubmitting.value = false
    }

    return {
      form,
      printIpAddressField,
      receiptPreviewBackgroundImageField,
      isSubmitting,
      updated,
      isConnected,
      conflictingKitchenPrinterNames,
      checkConnectionStatus,
      onPreviewKey,
      backgroundImagePreviewUrl,
      changePrintIpAddress,
      testConnection,
      previewBackgroundImage,
      lessThanOrEqualTo2Gb,
      hasError,
      printerCertUrl,
      printerWebConfigUrl,
      update,
      openPrinterDiagnostic,
      icons: {
        mdiCheckCircleOutline,
        mdiCloseCircleOutline,
        mdiPrinterSearch,
        mdiClose,
      },
    }
  },
})
</script>

<style scoped>
.preview-bg {
  width: 400px;
  height: 70vh;
  background-color: #fff;
  background-position: center;
  background-size: contain;
  background-repeat: no-repeat;
}
</style>
